// ConfettiBurst — lightweight, dependency-free celebration confetti.
// Feb 26 2026 · mounted behind the two avatars on every mutual celebration
// (Match, Meet, We-Met, Keep) to give the moment a proper "yes!" beat.
//
// How it works:
//   • Renders N tiny absolutely-positioned particles inside a full-cover div.
//   • Each particle picks a random angle, distance, size, colour and rotation
//     ONCE (via useMemo) and animates outward via CSS keyframes injected in
//     a <style> tag. No canvas, no rAF loop — pure CSS transform+opacity
//     hardware-accelerated for the ~2s the burst runs.
//   • Auto-remounts on each new burst via the `run` prop (bump a counter to
//     re-fire without unmounting the celebration container).

import { useMemo } from 'react';

const DEFAULT_COLORS = ['#B91C1C', '#F59E0B', '#15803D', '#1E4FD8', '#EC4899', '#8B5CF6'];

export default function ConfettiBurst({
  count = 36,
  colors = DEFAULT_COLORS,
  spread = 220,           // max travel distance in px from centre
  duration = 1600,        // ms
  run = 0,                // bump to re-fire (each new mount picks fresh positions)
  className = '',
  'data-testid': testId = 'confetti-burst',
}) {
  // Freeze random values per `run` so we don't re-shuffle mid-animation.
  const particles = useMemo(() => {
    return Array.from({ length: count }).map((_, i) => {
      const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.6;
      const dist = spread * (0.35 + Math.random() * 0.65);
      const dx = Math.cos(angle) * dist;
      const dy = Math.sin(angle) * dist;
      return {
        dx: dx.toFixed(1),
        dy: dy.toFixed(1),
        color: colors[Math.floor(Math.random() * colors.length)],
        size: 5 + Math.random() * 6,                // 5–11 px
        rot: (Math.random() * 720 - 360).toFixed(0),
        delay: (Math.random() * 120).toFixed(0),
        shape: Math.random() > 0.5 ? 'rect' : 'circle',
      };
    });
  }, [run, count, spread]);

  return (
    <div
      className={`pointer-events-none absolute inset-0 flex items-center justify-center overflow-visible ${className}`}
      aria-hidden="true"
      data-testid={testId}
    >
      <style>{`
        @keyframes cf-burst {
          0%   { transform: translate(0,0) rotate(0deg) scale(0.4); opacity: 0; }
          15%  { opacity: 1; }
          80%  { opacity: 1; }
          100% { transform: translate(var(--dx), var(--dy)) rotate(var(--rot)) scale(1); opacity: 0; }
        }
      `}</style>
      {particles.map((p, i) => (
        <span
          key={i}
          style={{
            position: 'absolute',
            width: p.size,
            height: p.shape === 'rect' ? p.size * 0.5 : p.size,
            backgroundColor: p.color,
            borderRadius: p.shape === 'rect' ? 1 : '50%',
            '--dx': `${p.dx}px`,
            '--dy': `${p.dy}px`,
            '--rot': `${p.rot}deg`,
            animation: `cf-burst ${duration}ms cubic-bezier(0.15, 0.7, 0.35, 1) ${p.delay}ms forwards`,
            willChange: 'transform, opacity',
          }}
        />
      ))}
    </div>
  );
}
